import { Form, notification } from "antd";
import { NewCivilDocument, NewCivilRequest } from "../../../models/new-civil/new-civil-request";
import { useNavigate } from "react-router";
import { useGetCivilCaseTypesQuery, useSubmitCivilEFileMutation } from "../../../state/e-file-api";
import { buildEFileOptionsRequest, buildCivilEFileRequest } from "../../../state/connect-api";
import { useEffect, useState } from "react";
import { NewCivilParty } from "../../../models/new-civil/new-civil-party";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store";
import useCivilEFilePayment from "./e-file-payment/useCivilEFilePayment";
import getConfigs from "../../../GetConfigs";

const useNewCivilCase = () => {

  const history = useNavigate();
  const dispatch = useDispatch();
  const { activeCaseNumber } = useSelector((state: RootState) => state.global);
  const [newCivilForm] = Form.useForm();
  const [plaintiffForm] = Form.useForm();
  const [defendantForm] = Form.useForm();
  const [defendantBusiness, setDefendantBusiness] = useState(false);
  const { data: civilCaseTypes, isLoading: caseTypesLoader } = useGetCivilCaseTypesQuery(buildEFileOptionsRequest());
  const [submitCivilEFile, { isLoading: submitEFileLoader }] = useSubmitCivilEFileMutation();
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [activeCaseType, setActiveCaseType] = useState<any>();
  const { handlePrePayment } = useCivilEFilePayment();
  const [activePlaintiffIndex, setActivePlaintiffIndex] = useState(0);
  const [activeDefendantIndex, setActiveDefendantIndex] = useState(0);
  const [plaintiffCount, setPlaintiffCount] = useState<number>(1);
  const [defendantCount, setdefendantCount] = useState<number>(1);

  const configurations = getConfigs([
    "cvAllowedDocumentExtensions",
    "cvAllowedDocumentSizeMB"
  ]);
  type ParticipantFormValues = { Participants: NewCivilParty[]; }



  const submitNewCivilCase = async () => {
    await newCivilForm.validateFields();
    checkForFormErrors(true);
    checkForFormErrors(false);
    const allowedExtensions = configurations?.cvAllowedDocumentExtensions?.split(",")?.map(s => s.trim().toLowerCase()) ?? [];
    const bannedExtensions = [
      //windows
      ".exe", ".msi", ".msix", ".sixbundle", ".com", ".bat", ".cmd", ".ps1", ".psm1", ".vbs", ".vbe", ".wsf", ".wsh", ".scr", ".pif", ".cpi", ".dll", ".sys",
      //Linux/Mac
      ".sh", ".bash", ".zsh", ".app", ".dmg", ".pkg", ".run", ".bin",
      //other
      ".jar", ".class",
      ".js", ".mjs"
    ]
    const bannedNames = ["CON", "PRN", "AUX", "NUL", "COM1", "COM2", "COM3", "COM4", "COM5", "COM6", "COM7", "COM8", "COM9", "COM�", "COM�", "COM�", "LPT1", "LPT2", "LPT3", "LPT4", "LPT5", "LPT6", "LPT7", "LPT8", "LPT9", "LPT�", "LPT�", "LPT�"]
    const getNameOkay = (filename: string) => {
      const baseName = filename.split(".")[0].toLowerCase().trim();
      const invalidChars = /[<>:"/\\|?*\x00-\x1F]/;
      return !(bannedNames.some(name => name == baseName) && invalidChars.test(baseName));
    }
    const getExtOkay = (filename: string) => {
      const n = filename.toLowerCase();
      return (allowedExtensions.length == 0 || allowedExtensions.some(ext => n.endsWith(ext))) && !bannedExtensions.some(ext => n.endsWith(ext));
    }
    const maxDocumentSize = Number(configurations?.cvAllowedDocumentSizeMB) * 1024 * 1024;
    const civilValues = await newCivilForm.validateFields();
    const civilRequest: NewCivilRequest = await newCivilForm.validateFields();
    const uploadItems = civilValues?.Documents?.fileList ?? [];
    const documents: File[] = uploadItems.map((x: any) => x.originFileObj as File).filter(Boolean);
    const errors: string[] = [];
    if (documents.length == 0 && activeCaseType.IsEFileAttachmentRequired == "Y") {
      errors.push(`You must have at least one document attached in order to EFile`);
    }
    const badType = documents.filter(d => !getExtOkay(d.name));
    const tooBig = documents.filter(d => d.size > maxDocumentSize);
    const badName = documents.filter(d => !getNameOkay(d.name));
    if (badType.length) {
      errors.push(...badType.map(d => `Invalid file type: ${d.name}. `));
      errors.push(`Allowed file types are ${configurations?.cvAllowedDocumentExtensions}`);
    }
    if (badName.length) {
      errors.push(...badName.map(d => `Invalid file name: ${d.name}. `));
      errors.push(`Please rename this file in order to upload`);
    }

    if (tooBig.length) {
      errors.push(...tooBig.map(d => `File too large: ${d.name}. `));
      errors.push(`Maximum file size is ${configurations?.cvAllowedDocumentSizeMB} MB`);

    }
    newCivilForm.setFields([
      {
        name: "Documents",
        errors,
      },
    ])
    if (errors.length > 0) {
      return;

    }
      const plaintiffValues = (await plaintiffForm.validateFields()) as ParticipantFormValues;
      const defendantValues = (await defendantForm.validateFields()) as ParticipantFormValues;
    const plaintiffs: NewCivilParty[] = (plaintiffValues.Participants ?? []).map((p, index) => ({
      ...p,
      DateOfBirth: plaintiffForm.getFieldValue(["Participants", index, 'DateOfBirth'])?.toDate?.() ?? null
    }));

    const defendants: NewCivilParty[] = (defendantValues.Participants ?? []).map((p, index) => ({
      ...p,
      DateOfBirth: defendantForm.getFieldValue(["Participants", index, 'DateOfBirth'])?.toDate?.() ?? null
    }));
    civilRequest.FilingDate = moment().toDate();
    civilRequest.Plaintiffs = plaintiffs;
    civilRequest.Defendants = defendants;
    civilRequest.Documents = await buildCivilDocuments(documents);
    const request = buildCivilEFileRequest(civilRequest);
    const response = await submitCivilEFile(request).unwrap();
    if (response?.IsSuccess) {
      if (activeCaseType.EFileFeeReq =="Y") {
        await handlePrePayment(response.CaseId, activeCaseType.FeeAmount, activeCaseType.EFileFeeTemplateFK);
        setIsModalVisible(true);
      }
      else {
      notification.success({ message: "Civil Case Submitted Successfully" });
      let route = "/home";
       history(route);
      }
    }
    else {
      notification.error({ message: "Error submitting Case" });
    }
  }
  const handlePlaintiffBusiness = (isBusiness: boolean, rowIndex: number) => {
    if (isBusiness) {
      const plaintiffs = (plaintiffForm.getFieldValue("Participants") ?? []) as any[];
      const next = [...plaintiffs];
      next[rowIndex].FirstName = "";
      next[rowIndex].DateOfBirth = null;
      plaintiffForm.setFieldsValue({ Plaintiffs: next });
    }
  }
  const handleDefendantBusiness = (isBusiness: boolean, rowIndex: number) => {
    if (isBusiness) {
      const defendants = (defendantForm.getFieldValue("Participants") ?? []) as any[];
      const next = [...defendants];
      next[rowIndex].FirstName = "";
      next[rowIndex].DateOfBirth = null;
      plaintiffForm.setFieldsValue({ Plaintiffs: next });
    }
    setDefendantBusiness(isBusiness);
  }
  const handleActiveCaseType = (caseTypePK) => {
    setActiveCaseType(civilCaseTypes.CaseTypes.find(caseType => caseType.CaseTypePK === caseTypePK));
    newCivilForm.setFields([{ name:"Documents", errors: []}]);
  }

  const goPrevPlaintiff = async () => {
    if (await checkForFormErrors(true)) {
      setActivePlaintiffIndex((i) => Math.max(0, i - 1));
    };
  }
  const goNextPlaintiff = async () => {
    if (await checkForFormErrors(true)) {
      setActivePlaintiffIndex((i) => Math.min(plaintiffCount - 1, i + 1));
    };
  }
  const addPlaintiff = async (add, currentCount: number) => {
    if (await checkForFormErrors(true)) {
      add(new NewCivilParty({ IsPlaintiff: true }));
      setActivePlaintiffIndex(currentCount);
      setPlaintiffCount(currentCount + 1);
    }
  };
  const deletePlaintiff = (remove, count: number, removedIndex: number) => {
    if (count <= 1) {
      return
    }
    remove(removedIndex);
    setPlaintiffCount(count - 1);
    setActivePlaintiffIndex(Math.min(removedIndex, (count - 2)));
    plaintiffForm.validateFields();
  }
  const goPrevDefendant = async () => {
    if (await checkForFormErrors(false)) {
      setActiveDefendantIndex((i) => Math.max(0, i - 1));
    }
  }
  const goNextDefendant = async () => {
    if (await checkForFormErrors(false)) {
      setActiveDefendantIndex((i) => Math.min(defendantCount - 1, i + 1));
    }
  }
  const addDefendant = async (add, currentCount: number) => {
    if (await checkForFormErrors(false)) {
      add(new NewCivilParty());
      setActiveDefendantIndex(currentCount);
      setdefendantCount(currentCount + 1);
    }

  };
  const deleteDefendant = (remove, count: number, removedIndex: number) => {
    console.log(removedIndex);
    if (count <= 1) {
      return
    }
    remove(removedIndex);
    setdefendantCount(count - 1);
    setActiveDefendantIndex(Math.min(removedIndex, (count - 2)));
  }

  const checkForFormErrors = async (isPlaintiff: boolean) => {
    //validates form for errors and navigates to the error if one exists. Navigation should only occur if the person changes to a different casetype after adding participants
    if (isPlaintiff) {
      try {
        return await plaintiffForm.validateFields();
      }
      catch (err) {
        const errorPlaintiffIndex = err.errorFields[err.errorFields.length - 1].name[1];
        if (activePlaintiffIndex != errorPlaintiffIndex) {
          setActivePlaintiffIndex(errorPlaintiffIndex);
          notification.error({ message: `There was an issue with plaintiff number ${errorPlaintiffIndex + 1}. Please fix the errors before continuing.` });
          await plaintiffForm.validateFields();
        }
      }
    }
    else {
      try {
        return await defendantForm.validateFields();
      }
      catch (err) {
        const errorDefendantIndex = err.errorFields[err.errorFields.length - 1].name[1];
        if (activeDefendantIndex != errorDefendantIndex) {
          setActiveDefendantIndex(errorDefendantIndex);
          notification.error({ message: `There was an issue with defendant number ${errorDefendantIndex + 1}. Please fix the errors before continuing.` });
          await defendantForm.validateFields();
        }
      }
    }
  }

  const buildCivilDocuments = async (documents: File[]):
    Promise<NewCivilDocument[]> => {
    const fileToBase64 = (file: File):
      Promise<string> => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
        reader.onload = () => {
          const result = String(reader.result);
          const base64 = result.split(",")[1] ?? "";
          resolve(base64);
        }
        reader.readAsDataURL(file);
      });
    return Promise.all(documents.map(async (file) => ({
      FileName: file.name,
      FileExtension: file.name.match(/\.[^.]+$/)?.[0] ?? null,
      FileSizeBytes: file.size,
      FileData: await fileToBase64(file),
    }))
    );
  };

  return { newCivilForm, submitNewCivilCase, civilCaseTypes, caseTypesLoader, handlePlaintiffBusiness, handleDefendantBusiness, defendantBusiness, plaintiffForm, defendantForm, isModalVisible, setIsModalVisible, handleActiveCaseType, activeCaseType, activePlaintiffIndex, plaintiffCount, goPrevPlaintiff, goNextPlaintiff, deletePlaintiff, addPlaintiff, activeDefendantIndex, defendantCount, goPrevDefendant, goNextDefendant, deleteDefendant, addDefendant, submitEFileLoader };

};

export default useNewCivilCase;
